import { DOCUMENT } from '@angular/common';
import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { MutationObserverService } from '@frontend/sports/common/core/utils/dom';
import { distinctUntilChanged, map, merge } from 'rxjs';

const MUTATION_OBSERVER_OPTIONS = { attributeFilter: ['class'] };

const SCROLL_BLOCK_CLASSES = ['modal-open', 'cdk-global-scrollblock'] as const;

/** @WhatItDoes Reports true if any of the known scroll block classes are present on the body or html element, otherwise false */
@Injectable({
    providedIn: 'root',
})
export class ScrollBlockObserverService {
    public readonly isBlocked: Signal<boolean>;

    readonly #document = inject(DOCUMENT);
    readonly #mutationObserverService = inject(MutationObserverService);

    constructor() {
        this.isBlocked = this.createScrollBlockSignal();
    }

    /** On any class mutation of the body or html element, check if the scroll blocking classes are present. */
    private createScrollBlockSignal(): Signal<boolean> {
        return toSignal(
            merge(
                this.#mutationObserverService.observe(this.#document.body, MUTATION_OBSERVER_OPTIONS),
                this.#mutationObserverService.observe(this.#document.documentElement, MUTATION_OBSERVER_OPTIONS),
            ).pipe(
                map(() => this.isScrollBlocked()),
                distinctUntilChanged(),
            ),
            { initialValue: false },
        );
    }

    private isScrollBlocked(): boolean {
        return SCROLL_BLOCK_CLASSES.some(
            (className) => this.#document.body.classList.contains(className) || this.#document.documentElement.classList.contains(className),
        );
    }
}
